import { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CheckoutContext from '../lib/CheckoutContext';
import { getCustomerMessageFromApolloError, getTokenFromApolloError, logInternalError } from '../lib/errors';
import * as types from '../types';
import { setGlobalVariablesForConversionTracking } from '../lib/analytics/analyticsUtils';

export type HandlePurchaseSuccess = (
  cart: types.Cart,
  cartPricing: types.CartPricing,
  invoiceNumber: string,
  isReturningCustomer: boolean,
  isGWP: boolean,
  applePayUserSession?: types.Session,
) => void;

interface UseHandlePurchaseProps {
  cartPricing: types.CartPricing;
  onError: (errorMessage: string, token?: string) => void;
  onSuccess: HandlePurchaseSuccess;
}

export default function useHandlePurchase({ cartPricing, onError, onSuccess }: UseHandlePurchaseProps) {
  const { t } = useTranslation();
  const { cart, checkoutState, purchaseMutationLoading, executePurchaseMutation } = useContext(CheckoutContext);

  // Exclude any invalid codes from the input
  const cartForPurchase: types.Cart = useMemo(
    () => ({
      ...cart,
      couponCode: cartPricing.validationErrors?.couponCode ? undefined : cart.couponCode,
      redeemedGiftCardCode: cartPricing.validationErrors?.redeemedGiftCardCode ? undefined : cart.redeemedGiftCardCode,
      referralCode: cartPricing.validationErrors?.referralCode ? undefined : cart.referralCode,
    }),
    [
      cart,
      cartPricing.validationErrors?.couponCode,
      cartPricing.validationErrors?.redeemedGiftCardCode,
      cartPricing.validationErrors?.referralCode,
    ],
  );

  const handlePurchase = useCallback(
    async (
      emailAddress: string | undefined,
      shippingCode?: types.ShippingCode,
      threeDSecureActionResultToken?: string,
      applePayUserSession?: types.Session,
    ) => {
      try {
        const result = await executePurchaseMutation(
          cartForPurchase,
          {
            ...checkoutState,
            shippingCode: shippingCode ?? checkoutState.shippingCode,
          },
          threeDSecureActionResultToken,
        );

        const { chargeInvoice, giftCardInvoices, isGWP, isReturningCustomer } = result;
        const invoiceNumber = chargeInvoice?.number ?? giftCardInvoices?.[0]?.number;

        const cartItemsArray = Object.values(cartForPurchase.cartItems);
        setGlobalVariablesForConversionTracking(invoiceNumber, cartItemsArray, cartPricing, emailAddress);

        onSuccess(cartForPurchase, cartPricing, invoiceNumber, isReturningCustomer, isGWP, applePayUserSession);
      } catch (err) {
        const customerMessage = getCustomerMessageFromApolloError(err);
        const token = getTokenFromApolloError(err);
        if (customerMessage) {
          onError(customerMessage, token);
        } else {
          logInternalError(err, {
            // For purchase errors, separate errors by the message
            fingerprint: ['{{ default }}', err.message],
            tags: {
              checkoutStep: 'purchase',
            },
          });
          onError(t('failed_to_process_purchase_please_try_again'), token);
        }
      }
    },
    [cartForPurchase, cartPricing, checkoutState, executePurchaseMutation, onError, onSuccess, t],
  );

  return {
    handlePurchase,
    purchaseMutationLoading,
  };
}
